import { useRouter } from '@tanstack/react-router';
import { Avatar, AvatarFallback, AvatarImage } from '@ui/avatar.tsx';
import { Button } from '@ui/button.tsx';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/tooltip.tsx';
import { ArrowLeftIcon, CircleCheck, CircleDot, CircleOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type UserProfileHeaderProps = {
  user: {
    firstname?: string;
    lastname?: string;
    avatar?: string | null;
  };
  isBlocked: boolean;
  isRegistered: boolean;
  isInactive: boolean;
  onToggleBlock: () => void;
  blockPending: boolean;
  disableBlockAction: boolean;
  /** Affiché au survol lorsque l’action est désactivée (ex. commercial sans droit de blocage). */
  blockDisabledTooltip?: string;
  backLabel: string;
};

export function UserProfileHeader({
  user,
  isBlocked,
  isRegistered,
  isInactive,
  onToggleBlock,
  blockPending,
  disableBlockAction,
  blockDisabledTooltip,
  backLabel
}: UserProfileHeaderProps) {
  const router = useRouter();
  const { t } = useTranslation();

  const blockButton = (
    <Button
      variant="outline"
      rounded="full"
      loading={blockPending}
      disabled={disableBlockAction}
      onClick={onToggleBlock}
    >
      {isBlocked
        ? t('admin.user.profile.action.unblock.contact')
        : t('admin.user.profile.action.block.contact')}
      {isBlocked ? <CircleCheck /> : <CircleOff />}
    </Button>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex gap-4 items-center">
          <Avatar size="sm" className="border border-border">
            <AvatarImage src={user.avatar ?? ''} alt={`${user.firstname} ${user.lastname}`} />
            <AvatarFallback text={`${user.firstname} ${user.lastname}`} initials={true} />
          </Avatar>

          <div className="space-y-2">
            <h1 className="text-4xl font-bold">{`${user.firstname} ${user.lastname}`}</h1>

            {isBlocked ? (
              <span className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-md">
                <CircleDot size={14} />
                <span>{t('admin.user.profile.status.blocked')}</span>
              </span>
            ) : isInactive ? (
              <span className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-md">
                <CircleDot size={14} />
                <span>{t('admin.user.profile.status.inactive')}</span>
              </span>
            ) : isRegistered ? (
              <span className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium text-emerald-600 bg-emerald-200 rounded-md">
                <CircleDot size={14} />
                <span>{t('admin.user.profile.status.registered')}</span>
              </span>
            ) : (
              <span className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-md">
                <CircleDot size={14} />
                <span>{t('admin.user.profile.status.pre.registered')}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <Button rounded="full" onClick={() => router.history.back()}>
          <ArrowLeftIcon />
          {backLabel}
        </Button>

        {disableBlockAction && blockDisabledTooltip ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">{blockButton}</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>{blockDisabledTooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          blockButton
        )}
      </div>
    </div>
  );
}
